// Implements: F-UIX-018, F-USG-005
/**
 * "Este mes" of My usage (UIX.4): the person's own month in each scope. Pure presentation.
 *
 * One card per scope the server lists (the personal scope and each organization), named
 * from the session's organizations; one the session no longer lists is named generically,
 * never by its id. Each card shows the count against the limit, the runs in flight, the
 * spend against the budget and what is left. Money is formatted here, in the backoffice,
 * and nowhere in the working views (OD-12, UIX.9). The monthly budgets carry no currency on
 * the wire: they are kept in micro-USD, the platform's price currency.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { moneyText } from "../../../shared/ui/moneyText";
import { renewalDateText } from "../../../shared/ui/molecules/usageRefusal";
import type { BackofficeScope } from "../../../types/api";
import type { MyUsageSummaryView, ScopeUsageView } from "../../../types/reports";
import { remainingOf, scopeLabelOf } from "../model/myUsage";
import type { ScopeLabel } from "../model/myUsage";
import styles from "./MyUsage.module.css";

/** The currency of the monthly budgets. */
export const BUDGET_CURRENCY = "USD";

export interface MyUsageMonthProps {
  readonly summary: MyUsageSummaryView;
  readonly scopes: readonly BackofficeScope[];
}

function ScopeCard({ usage, label }: { readonly usage: ScopeUsageView; readonly label: ScopeLabel }): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const remaining = remainingOf(usage);
  const name =
    label.kind === "organization"
      ? label.name
      : label.kind === "personal"
        ? t("backoffice.myUsage.scope.personal")
        : t("backoffice.myUsage.scope.formerOrganization");
  const money = (micros: number): string => moneyText(micros, BUDGET_CURRENCY);

  return (
    <li className={styles.card} aria-labelledby={headingId}>
      <h3 id={headingId} className={styles.cardTitle}>
        {name}
      </h3>
      <dl className={styles.figures}>
        <dt>{t("backoffice.myUsage.figure.generations")}</dt>
        <dd>
          {t("backoffice.myUsage.figure.generationsValue", {
            used: usage.generations_delivered,
            limit: usage.generations_limit,
          })}
        </dd>
        <dt>{t("backoffice.myUsage.figure.inFlight")}</dt>
        <dd>{usage.generations_in_flight}</dd>
        <dt>{t("backoffice.myUsage.figure.spent")}</dt>
        <dd>
          {t("backoffice.myUsage.figure.spentValue", {
            spent: money(usage.spent_micros),
            budget: money(usage.budget_limit_micros),
          })}
        </dd>
        <dt>{t("backoffice.myUsage.figure.remaining")}</dt>
        <dd>
          {t("backoffice.myUsage.figure.remainingValue", {
            generations: remaining.generations,
            budget: money(remaining.budgetMicros),
          })}
        </dd>
      </dl>
    </li>
  );
}

export function MyUsageMonth({ summary, scopes }: MyUsageMonthProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const renewal = renewalDateText(summary.renews_at);

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.sectionTitle}>
        {t("backoffice.myUsage.month.title")}
      </h2>
      {summary.enforced ? null : <p className={styles.note}>{t("backoffice.myUsage.month.notEnforced")}</p>}
      {renewal === null ? null : (
        <p className={styles.hint}>{t("backoffice.myUsage.month.renews", { date: renewal })}</p>
      )}
      {summary.scopes.length === 0 ? (
        <p className={styles.hint}>{t("backoffice.myUsage.month.empty")}</p>
      ) : (
        <ul className={styles.cards}>
          {summary.scopes.map((usage) => (
            <ScopeCard
              key={usage.organization_id ?? "personal"}
              usage={usage}
              label={scopeLabelOf(usage.organization_id, scopes)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
